import React, { useState, useEffect } from "react";
import { db } from "../../../../firebase";
import { Button, ModalFooter, Form, FormGroup, Label, Input, Row, Col, Spinner } from 'reactstrap';
import { ref, set, push, update, get, child } from "firebase/database";
import { errorToast, successToast } from "../../../common/toast";
import { sendRegistrationEmail, validateForm } from "../../../common/commonFunction";

const AddNewUser = ({ fetchUsers, setIsAddModel, editData }) => {
    const [formValue, setFormValue] = useState({
        name: "",
        address: "",
        email: "",
        number: "",
        plan: "",
        caller: "",
    });

    const [formErrors, setFormErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [employee, setEmployee] = useState([]);
    const [packages, setPackages] = useState([]);

    const fetchEmployee = async () => {
        const employeeRef = ref(db, "employees");
        const snapshot = await get(employeeRef);
        if (snapshot.exists()) {
            const userData = Object.entries(snapshot.val()).map(([key, value]) => ({
                value: key,
                label: value.name,
            }));
            setEmployee(userData);
        }
    };

    const fetchPackages = async () => {
        const packagesRef = ref(db, "packages");
        const snapshot = await get(packagesRef);
        if (snapshot.exists()) {
            const userData = Object.entries(snapshot.val()).map(([key, value]) => ({
                value: key,
                label: value.planName,
            }));
            setPackages(userData);
        }
    };

    const formValidationSchema = {
        name: {
            required: { message: "Name is required" },
        },
        email: {
            required: { message: "Email is required" },
            pattern: { regex: /\S+@\S+\.\S+/, message: "Email is invalid" },
        },
        address: {
            required: { message: "Address is required" },
        },
        number: {
            required: { message: "Phone number is required" },
            pattern: { regex: /^\d{10}$/, message: "Phone number must be 10 digits" },
        },
        plan: {
            required: { message: "Please select a plan" },
        },
        caller: {
            required: { message: "Please select a caller" },
        },
    };

    const generateOTP = () => {
        const otp = Math.floor(100000 + Math.random() * 900000);
        return otp.toString();
    };

    const handleFormValidation = () => {
        const errors = validateForm(formValue, formValidationSchema);
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        const otp = generateOTP();
        const payload = {
            ...formValue,
            registrationDate: new Date().toISOString(),
            otp: otp,
            startDate: "",
            status: "Pending",
        };

        if (handleFormValidation()) {
            setIsLoading(true);
            try {
                if (editData) {
                    const userRef = ref(db, `users/${editData.id}`);
                    await update(userRef, formValue);
                    successToast("User updated successfully!");
                } else {
                    const newUserRef = push(ref(db, "users"));
                    const newUserId = newUserRef.key;
                    await set(newUserRef, { ...payload, id: newUserId });

                    // Optionally add to qcReports
                    // const qcReportRef = ref(db, `qcReports/${newUserId}`);
                    // await set(qcReportRef, {
                    //     userId: newUserId,
                    //     name: payload.name,
                    //     email: payload.email,
                    //     number: payload.number,
                    //     startDate: "",
                    //     endDate: "",
                    //     totalForms: payload?.plan?.label,
                    //     savedForms: 0,
                    //     submittedForms: 0,
                    //     wrongForms: 0,
                    //     rightForms: 0,
                    //     registrationDate: payload?.registrationDate,
                    // });

                    successToast("User added successfully!");
                    sendRegistrationEmail({ ...payload, id: newUserId });
                }

                // Reset form fields
                setFormValue({
                    name: "",
                    address: "",
                    email: "",
                    number: "",
                    plan: "",
                    caller: "",
                });

                fetchUsers();
                setIsAddModel(false);
            } catch (error) {
                errorToast("Error saving user: " + error.message);
            } finally {
                setIsLoading(false);
            }
        }
    };

    const setValue = (key, value) => {
        setFormValue({ ...formValue, [key]: value });
    };

    useEffect(() => {
        if (editData) {
            setFormValue({
                name: editData?.name || "",
                address: editData?.address || "",
                email: editData?.email || "",
                number: editData?.number || "",
                plan: editData?.plan || "",
                caller: editData?.caller || "",
                startDate: editData?.startDate || "",
                endDate: editData?.endDate || "",
            });
        }
    }, [editData]);

    useEffect(() => {
        if (!employee?.length) {
            fetchEmployee();
        }
        if (!packages?.length) {
            fetchPackages();
        }
    }, []);

    return (
        <div>
            <Form>
                <Row>
                    <Col lg={4} md={6} sm={12}>
                        <FormGroup>
                            <Label for="name">Name</Label>
                            <Input
                                type="text"
                                id="name"
                                placeholder="Enter name"
                                value={formValue.name}
                                onChange={(e) => setValue("name", e.target.value)}
                                invalid={!!formErrors.name}
                            />
                            {formErrors.name && <span className="text-danger">{formErrors.name}</span>}
                        </FormGroup>
                    </Col>

                    <Col lg={4} md={6} sm={12}>
                        <FormGroup>
                            <Label for="email">Email</Label>
                            <Input
                                type="email"
                                id="email"
                                placeholder="Enter email"
                                value={formValue.email}
                                onChange={(e) => setValue("email", e.target.value)}
                                invalid={!!formErrors.email}
                            />
                            {formErrors.email && <span className="text-danger">{formErrors.email}</span>}
                        </FormGroup>
                    </Col>

                    <Col lg={4} md={6} sm={12}>
                        <FormGroup>
                            <Label for="address">Address</Label>
                            <Input
                                type="text"
                                id="address"
                                placeholder="Enter address"
                                value={formValue.address}
                                onChange={(e) => setValue("address", e.target.value)}
                                invalid={!!formErrors.address}
                            />
                            {formErrors.address && <span className="text-danger">{formErrors.address}</span>}
                        </FormGroup>
                    </Col>

                    <Col lg={4} md={6} sm={12}>
                        <FormGroup>
                            <Label for="number">Phone Number</Label>
                            <Input
                                type="text"
                                id="number"
                                placeholder="Enter phone number"
                                value={formValue.number}
                                onChange={(e) => setValue("number", e.target.value)}
                                invalid={!!formErrors.number}
                            />
                            {formErrors.number && <span className="text-danger">{formErrors.number}</span>}
                        </FormGroup>
                    </Col>

                    <Col lg={4} md={6} sm={12}>
                        <FormGroup>
                            <Label for="caller">Caller</Label>
                            <Input
                                type="select"
                                id="caller"
                                value={JSON.stringify(formValue.caller) || {}}
                                onChange={(e) => {
                                    const selectedValue = e.target.value;
                                    setValue("caller", selectedValue ? JSON.parse(selectedValue) : null);
                                }}
                                invalid={!!formErrors.caller}
                            >
                                <option value="">Select Caller</option>
                                {employee?.map((item) => {
                                    return (
                                        <option key={item.value} value={JSON.stringify(item)}>
                                            {item.label}
                                        </option>
                                    );
                                })}
                            </Input>
                            {formErrors.caller && <span className="text-danger">{formErrors.caller}</span>}
                        </FormGroup>
                    </Col>

                    <Col lg={4} md={6} sm={12}>
                        <FormGroup>
                            <Label for="plan">Plan</Label>
                            <Input
                                type="select"
                                id="plan"
                                value={JSON.stringify(formValue.plan || {})}
                                onChange={(e) => {
                                    const selectedValue = e.target.value;
                                    setValue("plan", selectedValue ? JSON.parse(selectedValue) : null);
                                }}
                                invalid={!!formErrors.plan}
                            >
                                <option value="">Select Package</option>
                                {packages?.map((item) => (
                                    <option key={item.value} value={JSON.stringify(item)}>
                                        {item.label}
                                    </option>
                                ))}
                            </Input>
                            {formErrors.plan && <span className="text-danger">{formErrors.plan}</span>}
                        </FormGroup>
                    </Col>

                    {editData ?
                        <>
                            <Col lg={4} md={6} sm={12}>
                                <FormGroup>
                                    <Label for="startDate">Select Start Date</Label>
                                    <Input
                                        type="date"
                                        id="startDate"
                                        value={formValue?.startDate}
                                        onChange={(e) => setValue("startDate", e.target.value)}
                                        min={new Date().toISOString().split("T")[0]}
                                    />
                                </FormGroup>
                            </Col>
                            <Col lg={4} md={6} sm={12}>
                                <FormGroup>
                                    <Label for="endDate">Select End Date</Label>
                                    <Input
                                        type="date"
                                        id="endDate"
                                        value={formValue?.endDate}
                                        onChange={(e) => setValue("endDate", e.target.value)}
                                        min={new Date().toISOString().split("T")[0]}
                                    />
                                </FormGroup>
                            </Col>
                        </>
                        : <></>}
                </Row>

                <ModalFooter>
                    <Button color="primary" onClick={onSubmit} disabled={isLoading}>
                        {isLoading && <Spinner size="sm" className="me-2" />}
                        {editData ? "Update" : "Submit"}
                    </Button>
                    <Button color="secondary" onClick={() => setIsAddModel(false)}>
                        Cancel
                    </Button>
                </ModalFooter>
            </Form>
        </div>
    );
};

export default AddNewUser;
